/**
 * The keys a player holds while playing, and where they are kept.
 *
 * Three places, one per kind of play:
 *
 *   preview    – an author testing their draft. Kept on this device only
 *                (localStorage, per scenario), NEVER on the account: a draft
 *                key id is not a published Key yet, and an author's real keys
 *                must not be touched by testing.
 *   anonymous  – a logged-out player. sessionStorage `userKeys`, as
 *                `{keyId, scenarioId}` entries, so a reload keeps them.
 *   account    – a signed-in player. The server (User.keys) is the record.
 *
 * Everything here is synchronous and React-free; the network side lives in
 * hooks/usePlayerKeys.js. Ids are compared as strings throughout.
 */

export const SESSION_KEYS_STORAGE = 'userKeys';
export const PREVIEW_KEYS_STORAGE = 'previewKeys';

export const KEY_STORES = Object.freeze({
    PREVIEW: 'preview',
    ANONYMOUS: 'anonymous',
    ACCOUNT: 'account',
});

const toId = (entry) => {
    if (entry && typeof entry === 'object') return String(entry._id ?? entry.id ?? '');
    return String(entry);
};

const uniqueIds = (list) => {
    const seen = new Set();
    return (Array.isArray(list) ? list : [])
        .map(toId)
        .filter((id) => id && id !== 'undefined' && id !== 'null' && !seen.has(id) && seen.add(id));
};

/** Which store a player's keys live in. */
export function keyStoreFor({ preview, userId }) {
    if (preview) return KEY_STORES.PREVIEW;
    return userId ? KEY_STORES.ACCOUNT : KEY_STORES.ANONYMOUS;
}

/**
 * What arriving at a situation, or picking a choice, does to the keys held.
 *
 * Keys taken away are applied after keys picked up, so if a situation somehow
 * does both to the same key (the editor never writes that), taking away wins.
 *
 * @param {Array<string|object>} held
 * @param {{grants?: Array, removes?: Array}} changes
 * @returns {{next: string[], gained: string[], lost: string[]}}
 */
export function applyKeyChanges(held, { grants = [], removes = [] } = {}) {
    const before = uniqueIds(held);
    const removing = new Set(uniqueIds(removes));
    const heldSet = new Set(before);

    const gained = uniqueIds(grants).filter((id) => !heldSet.has(id) && !removing.has(id));
    const lost = before.filter((id) => removing.has(id));
    const next = [...before.filter((id) => !removing.has(id)), ...gained];

    return { next, gained, lost };
}

// ---- storage ----------------------------------------------------------------

const readJson = (storage, key, fallback) => {
    try {
        const raw = storage.getItem(key);
        return raw ? JSON.parse(raw) : fallback;
    } catch (error) {
        return fallback;
    }
};

const writeJson = (storage, key, value) => {
    try {
        storage.setItem(key, JSON.stringify(value));
    } catch (error) {
        // Storage can be full or blocked; the keys still work for this page.
    }
};

const sessionStore = () => (typeof window !== 'undefined' ? window.sessionStorage : null);
const localStore = () => (typeof window !== 'undefined' ? window.localStorage : null);

/** `{keyId, scenarioId}` entries, tolerating anything malformed. */
function readSessionEntries() {
    const storage = sessionStore();
    if (!storage) return [];
    const entries = readJson(storage, SESSION_KEYS_STORAGE, []);
    return Array.isArray(entries)
        ? entries.filter((entry) => entry && entry.keyId).map((entry) => ({
            keyId: String(entry.keyId),
            scenarioId: entry.scenarioId ? String(entry.scenarioId) : null,
        }))
        : [];
}

function writeSessionEntries(entries) {
    const storage = sessionStore();
    if (storage) writeJson(storage, SESSION_KEYS_STORAGE, entries);
}

function readPreviewMap() {
    const storage = localStore();
    if (!storage) return {};
    const map = readJson(storage, PREVIEW_KEYS_STORAGE, {});
    return map && typeof map === 'object' && !Array.isArray(map) ? map : {};
}

/**
 * The keys held, from a local store. Account keys come from the server, so
 * this returns [] for them.
 *
 * Anonymous keys span every scenario played this session, as before: key ids
 * are unique, so another scenario's keys never match this one's choices.
 */
export function readLocalKeys(store, scenarioId) {
    if (store === KEY_STORES.PREVIEW) {
        return scenarioId ? uniqueIds(readPreviewMap()[String(scenarioId)]) : [];
    }
    if (store === KEY_STORES.ANONYMOUS) {
        return uniqueIds(readSessionEntries().map((entry) => entry.keyId));
    }
    return [];
}

/** Records keys gained and lost in a local store. No-op for account keys. */
export function saveLocalKeyChanges(store, scenarioId, { gained = [], lost = [] }) {
    if (store === KEY_STORES.PREVIEW) {
        if (!scenarioId) return;
        const map = readPreviewMap();
        const { next } = applyKeyChanges(map[String(scenarioId)], { grants: gained, removes: lost });
        map[String(scenarioId)] = next;
        const storage = localStore();
        if (storage) writeJson(storage, PREVIEW_KEYS_STORAGE, map);
        return;
    }
    if (store === KEY_STORES.ANONYMOUS) {
        const losing = new Set(uniqueIds(lost));
        const entries = readSessionEntries().filter((entry) => !losing.has(entry.keyId));
        const present = new Set(entries.map((entry) => entry.keyId));
        uniqueIds(gained).forEach((keyId) => {
            if (!present.has(keyId)) {
                entries.push({ keyId, scenarioId: scenarioId ? String(scenarioId) : null });
            }
        });
        writeSessionEntries(entries);
    }
}

/**
 * Forgets one scenario's keys in a local store and returns what is still held.
 * No-op (returns []) for account keys.
 */
export function clearLocalScenarioKeys(store, scenarioId) {
    if (store === KEY_STORES.PREVIEW) {
        const map = readPreviewMap();
        if (scenarioId && map[String(scenarioId)]) {
            delete map[String(scenarioId)];
            const storage = localStore();
            if (storage) writeJson(storage, PREVIEW_KEYS_STORAGE, map);
        }
        return [];
    }
    if (store === KEY_STORES.ANONYMOUS) {
        const remaining = readSessionEntries().filter(
            (entry) => entry.scenarioId !== String(scenarioId)
        );
        writeSessionEntries(remaining);
        return uniqueIds(remaining.map((entry) => entry.keyId));
    }
    return [];
}

export { uniqueIds as normalizeKeyIds };
